// Discussions tab for the Activity page.
// Authored and commented-on discussions are merged server-side because UNION semantics
// require two findMany calls; Prisma distinct is unreliable with skip/take. (ref: DL-004)
// Defers fetching until tab is active so users who never open this tab pay no round-trip
// cost; matches the lazy-load pattern in BookmarksContext.tsx. (ref: DL-003)
import { useQuery } from "wasp/client/operations";
import { getActivityDiscussions } from "wasp/client/operations";
import { DiscussionCard } from "../../discussions/DiscussionCard";
import { EmptyTabState } from "./EmptyTabState";
import { ErrorTabState } from "./ErrorTabState";

interface DiscussionsTabProps {
  isActive: boolean;
}

function DiscussionCardSkeleton() {
  return (
    <div className="rounded-xl border border-neutral-200 dark:border-neutral-700 p-5 animate-pulse">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-8 w-8 rounded-full bg-neutral-200 dark:bg-neutral-700"></div>
        <div className="h-4 bg-neutral-200 dark:bg-neutral-700 rounded-sm w-32"></div>
      </div>
      <div className="h-5 bg-neutral-200 dark:bg-neutral-700 rounded-sm w-3/4 mb-2"></div>
      <div className="space-y-2 mb-4">
        <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-sm w-full"></div>
        <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-sm w-4/5"></div>
      </div>
      <div className="flex gap-2 pt-3 border-t border-neutral-100 dark:border-neutral-800">
        <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-sm w-12"></div>
        <div className="h-3 bg-neutral-200 dark:bg-neutral-700 rounded-sm w-12"></div>
      </div>
    </div>
  );
}

export function DiscussionsTab({ isActive }: DiscussionsTabProps) {
  const { data, isLoading, error, refetch } = useQuery(
    getActivityDiscussions,
    undefined,
    { enabled: isActive },
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="w-full max-w-3xl">
            <DiscussionCardSkeleton />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return <ErrorTabState onRetry={() => refetch()} />;
  }

  if (!data || data.length === 0) {
    return (
      <EmptyTabState
        message="Join or start a conversation and it will show up here."
        actionLabel="Explore discussions"
        actionTo="/discussions"
      />
    );
  }

  return (
    <div className="flex flex-col items-center gap-4">
      {data.map((discussion) => (
        <div key={discussion.id} className="w-full max-w-3xl">
          <DiscussionCard discussion={discussion} />
        </div>
      ))}
    </div>
  );
}
